import React, { useState } from 'react';
import { Key, Check, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { ServiceCard } from './ServiceCard';

export const LicenceUsesCard = ({ tiers: usesCardTiers, onViewOffers, t }) => {
  const [showUsesDetails, setShowUsesDetails] = useState(false);
  const usesFromTier = usesCardTiers[0];
  const fmtPrice = (n) => n.toFixed(2).replace('.', ',');
  return (
    <ServiceCard testId="buy-licence-uses-card">
      <div className="group relative rounded-2xl p-6 md:p-8 border border-[rgba(29,233,208,0.15)] bg-gradient-to-b from-zinc-900/80 to-zinc-950/90 overflow-visible flex flex-col" data-testid="buy-licence-uses-card">
        {/* Badge Recharge */}
        <div data-testid="uses-recharge-badge" className="service-card-badge absolute -top-3 -right-3 bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-[10px] md:text-xs font-bold px-2.5 py-1.5 rounded-full shadow-lg flex items-center gap-1 z-10">
          <Key className="w-3 h-3" />{t('services.cards.usesRecharge')}
        </div>

        {/* En-tête */}
        <div className="relative flex items-center gap-3 mb-6">
          <div className="service-card-icon w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#1a2530] bg-gradient-to-br from-[#0f1620] to-[#0b0f15]">
            <Key className="w-5 h-5 text-[#1de9d0]" strokeWidth={2} />
          </div>
          <div>
            <h3 data-testid="uses-title" className="text-lg md:text-xl font-bold text-white leading-tight">{t('services.cards.usesTitle')}</h3>
            <p className="text-xs md:text-sm text-gray-500 mt-0.5">{t('services.cards.usesSubtitle')}</p>
          </div>
        </div>

        {/* Ligne info + détails dépliables (conservés) */}
        <div className="relative mb-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(29,233,208,0.08)] border border-[rgba(29,233,208,0.2)]"><Check className="w-2.5 h-2.5 text-[#1de9d0]" strokeWidth={3} /></span>
            <p className="text-zinc-300 text-xs md:text-sm leading-snug">{t('services.cards.usesContains')}</p>
          </div>
          <button type="button" onClick={() => setShowUsesDetails(v => !v)} data-testid="toggle-uses-details" aria-expanded={showUsesDetails} aria-controls="uses-details"
            className="group/details inline-flex items-center gap-1 text-[11px] md:text-xs text-zinc-500 hover:text-[#1de9d0] transition-colors font-medium">
            <ChevronRight className={`w-3 h-3 transition-transform duration-300 ${showUsesDetails ? 'rotate-90' : ''}`} strokeWidth={2.5} />
            {showUsesDetails ? t('services.cards.hideDetails') : t('services.cards.showDetails')}
          </button>
          <div id="uses-details" data-testid="uses-details" aria-hidden={!showUsesDetails}
            className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out ${showUsesDetails ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'}`}>
            <div className="overflow-hidden">
              <div className="rounded-lg bg-zinc-950/40 border border-[rgba(29,233,208,0.12)] p-3.5">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                  {(t('services.cards.usesDetailsList') || []).map((feat, i) => <li key={i} data-testid={`uses-detail-${i}`} className="flex items-start gap-2 text-[11px] md:text-xs text-zinc-300">
                    <Check className="w-3 h-3 text-[#1de9d0] flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="leading-snug">{feat}</span>
                  </li>)}
                </ul>
                <div className="mt-2.5 pt-2 border-t border-[rgba(29,233,208,0.12)] text-center"><span className="text-[11px] md:text-xs italic text-zinc-500">{t('services.cards.moreText')}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Prix */}
        <div className="relative mb-5">
          <div className="flex items-end gap-2 flex-wrap">
            <span className="text-5xl md:text-[44px] font-extrabold leading-none tabular-nums bg-gradient-to-b from-white to-[#9fb0c0] bg-clip-text text-transparent" data-testid="uses-card-from-price">{fmtPrice(usesFromTier.final)}</span>
            <span className="text-lg font-semibold text-gray-500 mb-1">€</span>
            {usesFromTier.discounted && <span data-testid="uses-original-price" className="text-sm text-gray-600 line-through mb-1.5">{fmtPrice(usesFromTier.original)}€</span>}
            <span className="text-xs md:text-sm text-gray-500 mb-1.5">{t('services.startingFrom')}</span>
            {usesFromTier.discounted && <span data-testid="uses-discount-badge" className="px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] md:text-xs font-bold mb-1.5">{usesFromTier.discLabel} CLIENT</span>}
          </div>
        </div>

        {/* Tiers au choix */}
        <div className="relative mb-5">
          <p className="text-[#1de9d0] text-xs md:text-sm font-semibold mb-3 flex items-center gap-2"><span className="text-base">🎟️</span> {t('services.cards.atChoice')}</p>
          <ul className="flex flex-col gap-2.5">
            {[...usesCardTiers.map(tier => ({ key: tier.id, node: <span>
              {tier.label} — <span className={tier.discounted ? 'text-emerald-300 font-semibold' : ''}>{fmtPrice(tier.final)}€</span>
              {tier.discounted && <span className="text-gray-500 line-through ml-1.5 text-[11px] md:text-xs">{fmtPrice(tier.original)}€</span>}{tier.star && ' ⭐'}
            </span> })), { key: 'equiv', node: <span className="text-[#1de9d0]/90 font-medium">{t('services.cards.usesEquiv')}</span> }, { key: 'auto', node: <span>{t('services.cards.usesAuto')}</span> }].map((feature) => (
              <li key={feature.key} data-testid={`uses-tier-${feature.key}`} className="service-card-feature flex items-center gap-3 text-xs md:text-sm text-gray-300">
                <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(29,233,208,0.08)] border border-[rgba(29,233,208,0.2)]"><Check className="w-2.5 h-2.5 text-[#1de9d0]" strokeWidth={3} /></span>{feature.node}
              </li>
            ))}
          </ul>
        </div>

        {/* Total */}
        <div data-testid="uses-total" className="relative mb-4 rounded-xl border border-[rgba(29,233,208,0.15)] bg-[rgba(29,233,208,0.03)] p-3 flex items-center justify-between mt-auto">
          <span className="text-gray-400 text-[11px] md:text-xs font-medium uppercase tracking-wider">{t('services.cards.fromLabel')}</span>
          <span className="flex items-baseline gap-1.5">
            {usesFromTier.discounted && <span className="text-sm text-gray-600 line-through">{fmtPrice(usesFromTier.original)}€</span>}
            <span className="text-2xl font-extrabold tabular-nums text-white">{fmtPrice(usesFromTier.final)}</span>
            <span className="text-base font-bold text-gray-500">€</span>
          </span>
        </div>

        {/* Bouton */}
        <Button onClick={onViewOffers} data-testid="goto-licences-btn"
          className="w-full font-bold py-3.5 rounded-xl transition-all duration-300 hover:-translate-y-0.5 text-sm md:text-base bg-gradient-to-r from-[#14b8c8] to-[#1de9d0] text-[#06181a] shadow-[0_0_25px_-8px_rgba(29,233,208,0.6)] hover:shadow-[0_0_35px_-6px_rgba(29,233,208,0.8)]">
          <span className="flex items-center justify-center gap-1">{t('services.cards.viewOffers')}<ChevronRight className="w-4 h-4" /></span>
        </Button>
      </div>
    </ServiceCard>
  );
};
